import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import {
  type IParamsKnowledgeBaseDocumentCreate,
  KnowledgeBaseApi,
  KnowledgeBaseDocument,
  KnowledgeBaseDocumentStatus,
} from 'entities/KnowledgeBase'
import { KnowledgeBasePath } from 'entities/ExportRouters'
import { MAX_FILES, STORAGE_IS_FULL } from 'pages/settings/pages/knowledgeBase/lib/constants'
import { type KnowledgeBaseDocumentsStore } from './knowledgeBaseDocumentsStore'
import { DocumentBlockerStore } from './documentBlockerStore'

const DEFAULT_TITLE = 'Untitled document'

export class KnowledgeBaseCreateDocumentStore {
  private _title = DEFAULT_TITLE
  private _html = ''
  private _isLoading = false
  private _isSavingLoading = false
  private _id: number | null = null
  private _documentBlockerStore: DocumentBlockerStore
  private _errorMessages: Record<string, string> = {}
  private _cacheParams: Omit<IParamsKnowledgeBaseDocumentCreate, 'draft'> | null = null
  private _documentStatus: KnowledgeBaseDocumentStatus | null = null
  private _isLoaded = false

  constructor(private _documentsStore: KnowledgeBaseDocumentsStore) {
    this._documentBlockerStore = new DocumentBlockerStore(this)
    makeAutoObservable(this)
  }

  get title() {
    return this._title
  }

  get html() {
    return this._html
  }

  get isLoading() {
    return this._isLoading
  }

  get isSavingLoading() {
    return this._isSavingLoading
  }

  get isDisabledSave() {
    return !this._title || this._isSavingLoading
  }

  get id() {
    return this._id
  }

  get params() {
    return {
      name: this._title,
      contents: this.htmlBody,
    }
  }

  get isExistChanges() {
    if (!this._cacheParams) return false
    return !isEqual(this._cacheParams, this.params)
  }

  get documentBlockerStore() {
    return this._documentBlockerStore
  }

  get errorMessages() {
    return this._errorMessages
  }

  get documentStatus() {
    return this._documentStatus
  }

  get isSavedDraftState() {
    return this._id && this._documentStatus === KnowledgeBaseDocumentStatus.Draft
  }

  get isLoaded() {
    return this._isLoaded
  }

  onCreateDocument = () => {
    this.setCacheParams({
      name: DEFAULT_TITLE,
      contents: this.htmlBody,
    })
  }

  handleRedirectToRoot = () => {
    uiStore.changeRouteViaVue({
      path: `${KnowledgeBasePath.root}`,
    })
  }

  onEditDocument = async (id: number) => {
    this._id = id

    if (this._isLoaded) return

    runInAction(() => {
      this._isLoading = true
    })

    try {
      const { data } = await KnowledgeBaseApi.viewDocument(id)

      if (data.is_locked) {
        const modalId = nanoid()

        modalStore.addModal({
          id: modalId,
          type: ModalTypeList.WARNING,
          showCloseButton: false,
          title: "Can't edit now",
          desc: 'Someone else is currently editing this document. Please try again later.',
          primaryAction: {
            text: 'Done',
            onAction: () => {
              this.handleRedirectToRoot()
              modalStore.closeModal(modalId)
            },
          },
        })

        return { isLocked: true }
      }

      await KnowledgeBaseApi.lockDocument(id)

      this.setCacheParams({
        name: data.title,
        contents: data.contents || '',
      })
      this.setTitle(data.title)
      this.setHtml(data.contents || '')
      this.setDocumentStatus(data.status)
      this._isLoaded = true

      return { isLocked: false }
    } catch (error) {
      logger.error(error)

      this.handleRedirectToRoot()

      return { isLocked: false }
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  onClosePage = async () => {
    runInAction(() => {
      this._isSavingLoading = true
    })

    try {
      await this._documentsStore.loadDocuments()
    } finally {
      runInAction(() => {
        this._isSavingLoading = false
      })
    }

    this.handleRedirectToRoot()
  }

  setTitle = (value: string) => {
    this._title = value
    this.setErrorMessage('title', '')
  }

  setHtml = (value: string) => {
    value = value.replace(/<html><body>/, '').replace(/<\/body><\/html>/, '')
    value = value === '<br>' ? '' : value
    this._html = value
    this.setErrorMessage('contents', '')
  }

  setCacheParams = (value: Omit<IParamsKnowledgeBaseDocumentCreate, 'draft'>) => {
    this._cacheParams = value
  }

  get htmlBody() {
    return `<html><body>${this._html}</body></html>`
  }

  setErrorMessage = (key: string, error: string) => {
    this._errorMessages[key] = error
  }

  setDocumentStatus = (status: KnowledgeBaseDocumentStatus) => {
    this._documentStatus = status
  }

  onHandleSave = async (isDraft: boolean) => {
    const knowledgeBase = this._documentsStore.knowledgeBase

    if (!this._id && knowledgeBase && knowledgeBase.documentsCount === MAX_FILES) {
      this.setErrorMessage('contents', STORAGE_IS_FULL)
      return
    }

    runInAction(() => {
      this._isSavingLoading = true
    })

    const payload = {
      ...this.params,
      draft: isDraft,
    }

    try {
      const { data: docResponse } = this._id
        ? await KnowledgeBaseApi.updateDocument(this._id, payload)
        : await KnowledgeBaseApi.createDocument(payload)

      toastStore.add({
        type: 'success',
        title: this._id ? 'Changes saved' : 'Document created',
      })

      this.setCacheParams(this.params)
      this.setDocumentStatus(docResponse.status)

      setTimeout(async () => {
        if (!isDraft) this.onClosePage()
        const document = new KnowledgeBaseDocument(docResponse)
        this._documentsStore.handleUploadingDocument(document)

        if (isDraft && !this._id) {
          uiStore.changeRouteViaVue({
            path: `${KnowledgeBasePath.editDocument}/${docResponse.id}`,
          })
          this._id = docResponse.id
          await KnowledgeBaseApi.lockDocument(docResponse.id)
        }
      })

      return true
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('contents')) {
          this.setErrorMessage('contents', 'The contents should not contain empty data.')
        }
        if (e.errors.getError('name')) {
          this.setErrorMessage('title', e.errors.getError('name'))
        }
      }
      logger.error(e)
      return false
    } finally {
      runInAction(() => {
        this._isSavingLoading = false
      })
    }
  }

  onUnlockDocument = async () => {
    if (!this._id) return
    await KnowledgeBaseApi.unlockDocument(this._id)
  }

  onReset = async () => {
    await this.onUnlockDocument()
    this._id = null
    this._title = DEFAULT_TITLE
    this._html = ''
    this._errorMessages = {}
    this._documentStatus = null
    this._isLoaded = false
  }
}
